import type { MastraDBMessage } from "@mastra/core/agent/message-list";
import type { StorageThreadType } from "@mastra/core/memory";
import type { Draft } from "@reduxjs/toolkit";
import type { FetchArgs } from "@reduxjs/toolkit/query";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
} from "../helpers/tags.js";
import { api } from "../redux.js";
import { mastraBaseUrl } from "../shared.js";

// Thread endpoints return ISO strings for `createdAt`/`updatedAt`, but we declare
// `StorageThreadType`, which types both as `Date` -- it's Mastra's canonical shape and
// what these queries have always exposed. Consumers treat both as `Date | string`.

// Response shape of the thread listing, mirroring Mastra's own paginated
// thread routes (timestamps arrive as ISO strings, per the note above).
type MastraThreadListResponse = {
  threads: StorageThreadType[];
  total: number;
  page: number;
  perPage: number | false;
  hasMore: boolean;
};

const THREADS_PER_PAGE = 50;

export interface MastraThreadListQueryArg {
  agentId: string;
  resourceId: string;
  /** Blank or absent lists every chat; otherwise matches titles and message text. */
  query?: string;
}

/** Mastra's own routes mount under `/api`; our custom agent-api ones mount at the root. */
function mastraMemoryThreadUrl(threadId: string, suffix = ""): string {
  return `${mastraBaseUrl}/api/memory/threads/${encodeURIComponent(threadId)}${suffix}`;
}

// These endpoints target agent-api rather than the api the shared `baseQuery`
// points at, which `fetchBaseQuery` allows because absolute URLs bypass its
// `baseUrl`. Everything else about the base query still applies, including the
// bearer token agent-api authenticates with. They send no cookies: agent-api
// reads none, and a credentialed request is refused cross-origin by browsers
// unless the response allows credentials.
const mastraThreadApi = api
  .enhanceEndpoints({
    addTagTypes: ["MastraThread"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      allMastraThreads: builder.infiniteQuery<
        MastraThreadListResponse,
        MastraThreadListQueryArg,
        number
      >({
        infiniteQueryOptions: {
          initialPageParam: 0,
          getNextPageParam: (lastPage) =>
            lastPage.hasMore ? lastPage.page + 1 : undefined,
          getPreviousPageParam: () => undefined,
        },
        // Lists chats through agent-api's own `/copilot/threads` route
        // rather than Mastra's `listMemoryThreads`, which can neither search nor
        // exclude archived threads (it filters by `resourceId` and exact
        // metadata only).
        query: ({ queryArg, pageParam }) => ({
          url: `${mastraBaseUrl}/copilot/threads`,
          params: {
            agentId: queryArg.agentId,
            resourceId: queryArg.resourceId,
            page: pageParam,
            perPage: THREADS_PER_PAGE,
            query: queryArg.query?.trim() || undefined,
          },
        }),
        providesTags: (result, error, { agentId }) =>
          getTagsProvidedByList(
            "MastraThread",
            result?.pages.flatMap((page) => page.threads),
            { filteredBy: [agentId] },
          ),
      }),

      // No tags: its lazy trigger always refetches, so a chat created in the
      // transcript is found on the next open.
      latestGradingMastraThread: builder.query<
        StorageThreadType | null,
        { agentId: string; resourceId: string; gradingId: GradingId }
      >({
        query: (queryArg) => ({
          url: `${mastraBaseUrl}/copilot/threads`,
          params: { ...queryArg, orderBy: "updatedAt", perPage: 1 },
        }),
        transformResponse: (response: MastraThreadListResponse) =>
          response.threads[0] ?? null,
      }),

      mastraThread: builder.query<
        StorageThreadType,
        { agentId: string; threadId: string }
      >({
        query: ({ agentId, threadId }) => ({
          url: mastraMemoryThreadUrl(threadId),
          params: { agentId },
        }),
        providesTags: (result, error, { threadId }) =>
          getTagsProvidedByEntity("MastraThread", threadId),
      }),

      // Loaded imperatively by `useMastraChat` on every thread switch, so it
      // carries no tags: `useChat` owns the message list once history lands.
      mastraThreadMessages: builder.query<
        { messages: MastraDBMessage[] },
        { agentId: string; threadId: string }
      >({
        query: ({ agentId, threadId }) => ({
          url: mastraMemoryThreadUrl(threadId, "/messages"),
          params: { agentId },
        }),
      }),

      createMastraThread: builder.mutation<
        StorageThreadType,
        {
          agentId: string;
          resourceId: string;
          metadata: Record<string, unknown>;
        }
      >({
        query: (body) => ({
          url: `${mastraBaseUrl}/api/memory/threads`,
          method: "POST",
          params: { agentId: body.agentId },
          body,
        }),
        invalidatesTags: (result, error, { agentId }) =>
          getTagsInvalidatedByCreate("MastraThread", {
            inLists: [{ filteredBy: [agentId] }],
          }),
        onQueryStarted: async (
          { agentId, resourceId },
          { dispatch, getState, queryFulfilled },
        ) => {
          try {
            const { data: newThread } = await queryFulfilled;

            // Add the new thread to the beginning of the list. The list is
            // cached per search query, so find the entries rather than
            // rebuilding the args — and skip the ones showing search results,
            // where a thread with no title and no messages yet can't belong.
            const cachedLists = mastraThreadApi.util.selectInvalidatedBy(
              getState(),
              [buildListTag("MastraThread", [agentId])],
            );
            for (const cachedList of cachedLists) {
              if (cachedList.endpointName !== "allMastraThreads") {
                continue;
              }

              const listArgs =
                cachedList.originalArgs as MastraThreadListQueryArg;
              if (
                listArgs.resourceId !== resourceId ||
                (listArgs.query?.trim() ?? "") !== ""
              ) {
                continue;
              }

              dispatch(
                mastraThreadApi.util.updateQueryData(
                  "allMastraThreads",
                  listArgs,
                  (draft) => {
                    const firstPage = draft.pages[0];
                    if (firstPage) {
                      firstPage.threads = [newThread, ...firstPage.threads];
                      firstPage.total += 1;
                    }
                  },
                ),
              );
            }
          } catch {
            // If the mutation fails, don't update the cache
          }
        },
      }),

      updateMastraThread: builder.mutation<
        StorageThreadType,
        {
          agentId: string;
          threadId: string;
          title?: string;
          metadata?: Record<string, unknown>;
        }
      >({
        // Mastra's PATCH route replaces `metadata` wholesale (`metadata: metadata
        // || thread.metadata`), so a partial update has to read the current row
        // and merge client-side. That makes this two requests and so a `queryFn`,
        // though both still run through `baseQuery`. The route ignores every
        // other field -- it re-reads and spreads the thread itself -- so only
        // `title` and `metadata` are worth sending.
        queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
          const request: FetchArgs = {
            url: mastraMemoryThreadUrl(queryArg.threadId),
            params: { agentId: queryArg.agentId },
          };

          const existing = await baseQuery(request);
          if (existing.error) {
            return { error: existing.error };
          }
          const existingThread = existing.data as StorageThreadType;

          const updated = await baseQuery({
            ...request,
            method: "PATCH",
            body: {
              title: queryArg.title ?? existingThread.title ?? "",
              metadata: {
                ...existingThread.metadata,
                ...queryArg.metadata,
              },
            },
          });
          if (updated.error) {
            return { error: updated.error };
          }
          return { data: updated.data as StorageThreadType };
        },
        invalidatesTags: (result, error, { agentId, threadId }) =>
          getTagsInvalidatedByUpdate("MastraThread", threadId, {
            inLists: [{ filteredBy: [agentId] }],
          }),
        onQueryStarted: async (
          queryArg,
          { dispatch, getState, queryFulfilled },
        ) => {
          const { agentId, threadId } = queryArg;
          const optimisticallyUpdateThread = (
            draft: StorageThreadType | Draft<StorageThreadType>,
          ) => {
            if (queryArg.title !== undefined) {
              draft.title = queryArg.title;
            }
            if (queryArg.metadata !== undefined) {
              draft.metadata = {
                ...draft.metadata,
                ...queryArg.metadata,
              };
            }
          };

          const patches: { undo: () => void }[] = [];

          // Optimistically update single thread query cache
          patches.push(
            dispatch(
              mastraThreadApi.util.updateQueryData(
                "mastraThread",
                { agentId, threadId },
                optimisticallyUpdateThread,
              ),
            ),
          );

          // Optimistically update query cache for all thread lists (typically just one) that might contain this thread
          // This is so that clicking the "Archive" button on a thread feels instantaneous
          const cachedLists = mastraThreadApi.util.selectInvalidatedBy(
            getState(),
            [buildListTag("MastraThread", [agentId])],
          );
          for (const cachedList of cachedLists) {
            patches.push(
              dispatch(
                mastraThreadApi.util.updateQueryData(
                  "allMastraThreads",
                  cachedList.originalArgs,
                  (draft) => {
                    for (const page of draft.pages) {
                      const thread = page.threads.find(
                        (t) => t.id === threadId,
                      );
                      if (thread) {
                        optimisticallyUpdateThread(thread);
                        break;
                      }
                    }
                  },
                ),
              ),
            );
          }

          try {
            await queryFulfilled;
          } catch {
            // If the mutation fails, undo the optimistic updates
            for (const patch of patches) {
              patch.undo();
            }
          }
        },
      }),
    }),
  });

export const {
  useAllMastraThreadsInfiniteQuery,
  useLazyLatestGradingMastraThreadQuery,
  useMastraThreadQuery,
  useLazyMastraThreadQuery,
  useLazyMastraThreadMessagesQuery,
  useCreateMastraThreadMutation,
  useUpdateMastraThreadMutation,
} = mastraThreadApi;

export function invalidateMastraThreadAction(thread: {
  agentId: string;
  threadId: string;
}) {
  return mastraThreadApi.util.invalidateTags(
    getTagsInvalidatedByUpdate("MastraThread", thread.threadId, {
      inLists: [{ filteredBy: [thread.agentId] }],
    }),
  );
}
